import type { Organizer } from '@eventflow/shared-types';
import { type PayloadAction, createSlice } from '@reduxjs/toolkit';
import type { User } from '@supabase/supabase-js';

// Tipos de roles del sistema
export type UserRole = 'client' | 'organizer' | 'superadmin';

export interface AuthState {
  user: User | null;
  organizer: Organizer | null;
  roles: UserRole[];
  isLoading: boolean;
  isAuthenticated: boolean;
}

const initialState: AuthState = {
  user: null,
  organizer: null,
  roles: [],
  isLoading: true,
  isAuthenticated: false,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setUser: (
      state,
      action: PayloadAction<{
        user: User | null;
        organizer: Organizer | null;
        roles?: UserRole[];
      }>
    ) => {
      state.user = action.payload.user;
      state.organizer = action.payload.organizer;
      state.roles = action.payload.roles || [];
      state.isAuthenticated = !!action.payload.user;
      state.isLoading = false;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    logout: (state) => {
      state.user = null;
      state.organizer = null;
      state.roles = [];
      state.isAuthenticated = false;
      state.isLoading = false;
    },
  },
});

// Selectores helper
export const selectIsOrganizer = (state: { auth: AuthState }) =>
  state.auth.roles.includes('organizer') || state.auth.roles.includes('superadmin');

export const selectIsSuperadmin = (state: { auth: AuthState }) =>
  state.auth.roles.includes('superadmin');

export const selectIsClient = (state: { auth: AuthState }) =>
  state.auth.roles.includes('client') &&
  !state.auth.roles.includes('organizer') &&
  !state.auth.roles.includes('superadmin');

export const { setUser, setLoading, logout } = authSlice.actions;
export default authSlice.reducer;
